import { captureMessage, captureException, getCurrentHub } from '@sentry/vue';
import { OUTFIT_CATEGORY_KEY } from '@/constants';
import bottomBarCategories from '../fixtures/categories.json';
import categoriesDetails from '../fixtures/category_details.json';
import Blizzard from '@/services/Blizzard';
import UrlHelper from '@/services/UrlHelper';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import CategoryAssetsOverrideHelper from '@/services/CategoryAssetsOverrideHelper';
import { FlowModeEnum } from '@/enum/flow_mode';
import { Span } from '@sentry/tracing';
import { StyleEnum } from '@/enum/style';
import { GenderEnum } from '@/enum/gender';

import FeatureGating from './FeatureGating';
import { snapchat } from '@/gen/proto/protos';
import AssetsV3 = snapchat.bitmoji.api.AssetsV3;
import IAssetsV3 = snapchat.bitmoji.api.IAssetsV3;
import ITraitCategories = snapchat.bitmoji.api.ITraitCategories;
import ITraitCategory = snapchat.bitmoji.api.ITraitCategory;
import IMixAndMatchItemsMetadata = snapchat.bitmoji.api.IMixAndMatchItemsMetadata;
import IDefaultOptions = snapchat.bitmoji.api.IDefaultOptions;
import IFacialFeatureMetadata = snapchat.bitmoji.api.IFacialFeatureMetadata;

interface BottomBarCategory {
  iconName: string;
  subCategoryKeys: string[];
}

interface CategoryDetails {
  category_key: string;
  column_count: number;
  display_type: string;
  rotation: number;
}

const DEFAULT_OUTFIT_ID: number = 1018392;

// Inline of ./public/polyfills/array-flat.min.js since samsung devices still
// crash despite the polyfill https://jira.sc-corp.net/browse/BM-9298.
const arrayFlat = (r: any[]): any[] => {
  return r.reduce((r: any[], next: any[]) => {
    return Array.isArray(next) ? r.concat(arrayFlat(next)) : r.concat(next);
  }, []);
};

const loadBackupJson = (): Promise<IAssetsV3> => {
  // Dynamic import returns a promise
  // - https://developers.google.com/web/updates/2017/11/dynamic-import
  return import(
    /* webpackChunkName: "assets" */
    /* webpackMode: "lazy" */
    '../fixtures/assetsV3b64.json' //
  )
    .then((jsonObject: Record<string, string>) => {
      return AssetsV3.decode(new Uint8Array(_base64ToArrayBuffer(jsonObject.data)));
    })
    .catch((e) => {
      // This case should never happen as the import is importing a static file,
      // however, throw this error if something goes wrong with importing
      throw new Error(`Fetch Assets failed and static assets failed ${{ error: e }}`);
    });
};

const fetchAssets = (genderInt: GenderEnum, styleInt: StyleEnum): Promise<IAssetsV3> => {
  let span: Span;
  if (global.shouldTrace) {
    const transaction = getCurrentHub()
      .getScope()
      ?.getTransaction();

    if (transaction) {
      span = transaction.startChild();
      span.op = 'assets_v3';
    }
  }

  const assetsRequestStartMs = Date.now();
  return fetch(UrlHelper.getAssetUrl(genderInt, styleInt))
    .then((response: Response) => {
      Blizzard.recordAssetsRequestLatency(Date.now() - assetsRequestStartMs);
      if (span) {
        span.finish();
      }
      if (response.ok) {
        return response.arrayBuffer().then((responseBuffer) => {
          return AssetsV3.decode(new Uint8Array(responseBuffer));
        });
      }
      captureMessage(`Response status was not healthy ${response}`);
      return loadBackupJson();
    })
    .catch((e) => {
      // Report error and load backup json
      captureException(e);
      return loadBackupJson();
    });
};

const genderIntToString = (genderAsInt: number): GenderType => {
  const genders: Record<number, GenderType> = { 1: 'male', 2: 'female' };
  return genders[genderAsInt];
};

const styleIntToString = (styleAsInt: number): StyleType => {
  const styles: Record<number, StyleType> = { 1: 'bitstrips', 4: 'bitmoji', 5: 'cm' };
  return styles[styleAsInt];
};

// Use this to access bottomBarCategories instead of reading from it directly.
// bottomBarCategories only supports the flow modes "create" and "edit" but we
// need to support more e.g. "outfit"
const getBottomBarCategories = (
  style: StyleType,
  flowMode: FlowModeEnum,
  gender: GenderType,
): BottomBarCategory[] => {
  if (flowMode !== FlowModeEnum.CREATE) {
    flowMode = FlowModeEnum.EDIT;
  }

  const bottomBarCategoriesSubset: BottomBarCategory[] =
    bottomBarCategories[style][flowMode][gender];

  return bottomBarCategoriesSubset;
};

const loadAssetsData = (
  genderInt: GenderEnum,
  styleInt: StyleEnum,
  flowMode: FlowModeEnum,
  assetOverrides: string[],
): Promise<ProcessedAssetsData> => {
  return fetchAssets(genderInt, styleInt)
    .then((assets: IAssetsV3) => {
      return CategoryAssetsOverrideHelper.applyAssetOverrides(assetOverrides, assets);
    })
    .then((overridedAssets: IAssetsV3) => {
      return processAssets(genderInt, styleInt, flowMode, overridedAssets);
    })
    .catch((e) => {
      throw new Error(`Load Assets Data failed ${e}`);
    });
};

const processAssets = (
  genderInt: GenderEnum,
  styleInt: StyleEnum,
  flowMode: FlowModeEnum,
  assets: IAssetsV3,
): ProcessedAssetsData => {
  const gender: GenderType = genderIntToString(genderInt);
  const style: StyleType = styleIntToString(styleInt);
  let categoriesData: ITraitCategories | null | undefined =
    assets?.traits?.[gender]?.[style]?.categories;
  if (!categoriesData) {
    throw new Error(`[CategoryAssets] no trait categories available for: ${gender} ${style}`);
  }

  const allCategoryKeysSorted = arrayFlat(
    getBottomBarCategories(style, flowMode, gender).map(
      (masterCategory) => masterCategory.subCategoryKeys,
    ),
  );

  let sortedCategoriesData: AllCategories[] = [];

  if (categoriesData?.trait_category) {
    const traitCategoriesData: ITraitCategory[] = categoriesData.trait_category
      // take care of piercing
      .concat(FacialFeaturesHelper.getPiercingCategoriesDataFromAssets(gender, style, assets))
      .filter((a) => allCategoryKeysSorted.indexOf(a.key) !== -1)
      .filter((a) => {
        return a?.trait_category_options?.length || 0;
      })
      .sort((a, b) => {
        return allCategoryKeysSorted.indexOf(a.key) - allCategoryKeysSorted.indexOf(b.key);
      });
    sortedCategoriesData.push(...traitCategoriesData);
  }

  // Adding the outfit and save category to the category data
  const outfitCategoryOptions = assets?.outfits?.[gender]?.brands;
  if (!outfitCategoryOptions) {
    throw new Error(`[CategoryAssets] no outfit options available for: ${gender} ${style}`);
  }
  const outfitCategory: OutfitCategory = {
    key: OUTFIT_CATEGORY_KEY,
    options: outfitCategoryOptions,
  };
  sortedCategoriesData.push(outfitCategory);

  // Add the mix and match category data to the sortedCategoriesData
  const mixAndMatchCategoriesData = MixAndMatchHelper.getMixAndMatchCategoriesDataFromAssets(
    gender,
    assets,
  );
  if (mixAndMatchCategoriesData) {
    for (const cat of mixAndMatchCategoriesData) {
      if (DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.indexOf(cat.key) >= 0) {
        sortedCategoriesData.push(cat);
      }
    }
  }

  const traitsMetadata = assets?.traits?.metadata;
  if (!traitsMetadata) {
    throw new Error(`[CategoryAssets] no Traits metadata available`);
  }

  const mixAndMatchMetadata: IMixAndMatchItemsMetadata | null | undefined =
    assets?.mix_and_match_items?.metadata;
  if (!mixAndMatchMetadata) {
    throw new Error(`[CategoryAssets] no MixAndMatch metadata available`);
  }

  const facialFeaturesMetadata: IFacialFeatureMetadata | null | undefined =
    assets?.facial_features?.metadata;

  const myClosetCategory = {
    key: 'my_closet',
    trait_category_options: [],
  };
  sortedCategoriesData.push(myClosetCategory);

  const saveCategory = { key: 'save' };
  if (flowMode === FlowModeEnum.CREATE) {
    sortedCategoriesData.push(saveCategory);
  }

  const defaults: IDefaultOptions | null | undefined =
    assets?.defaults?.[gender]?.[style]?.option_ids;
  if (!defaults) {
    throw new Error(`[CategoryAssets] no Defaults metadata available`);
  }

  return {
    categories: sortedCategoriesData,
    metadata: traitsMetadata,
    defaults: defaults,
    mixAndMatchMetadata: mixAndMatchMetadata,
    facialFeaturesMetadata,
  };
};

const getCategoryList = (
  gender: GenderEnum,
  style: StyleEnum,
  flowMode: FlowModeEnum,
): BottomBarCategory[] => {
  const genderAsString = genderIntToString(gender);
  const styleAsString = styleIntToString(style);

  return getBottomBarCategories(styleAsString, flowMode, genderAsString);
};

const getFlatCategoryList = (
  gender: GenderEnum,
  style: StyleEnum,
  flowMode: FlowModeEnum,
): string[] => {
  const genderAsString = genderIntToString(gender);
  const styleAsString = styleIntToString(style);

  return arrayFlat(
    getBottomBarCategories(styleAsString, flowMode, genderAsString).map(
      (masterCategory) => masterCategory.subCategoryKeys,
    ),
  );
};

const getCategoryIndex = (
  categoryKey: string,
  gender: GenderEnum,
  style: StyleEnum,
  flowMode: FlowModeEnum,
): number => {
  return getFlatCategoryList(gender, style, flowMode).indexOf(categoryKey);
};

const getCategoryDetails = (categoryKey: string): CategoryDetails => {
  return categoriesDetails[categoryKey];
};

// Adapted from: https://stackoverflow.com/questions/21797299/convert-base64-string-to-arraybuffer
const _base64ToArrayBuffer = (base64: string) => {
  let binary_string = atob(base64);
  let len = binary_string.length;
  let bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
};

export default {
  DEFAULT_OUTFIT_ID,
  loadAssetsData,
  getCategoryList,
  getFlatCategoryList,
  getCategoryIndex,
  getCategoryDetails,
  styleIntToString,
};
